import { useEffect, useState } from "react";
import { Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CONFIRMATION_OPTIONS = ["confirmed", "unconfirmed"];

export function InternalSubmissionDialog({ open, onClose, onDone, jobId, jobTitle }) {
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidateId, setCandidateId] = useState("");
  const [selectedJobId, setSelectedJobId] = useState(jobId || "");
  const [readiness, setReadiness] = useState(null);
  const [form, setForm] = useState({
    resume_document_id: "",
    summary_version_id: "",
    screening_version_id: "",
    salary_confirmation: "",
    availability_confirmation: "",
    submitted_comp: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSelectedJobId(jobId || "");
  }, [jobId, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    api
      .get("/candidates", { params: { page_size: 100 } })
      .then(({ data }) => setCandidates(data.items || []))
      .catch(() => setCandidates([]));
    if (!jobId) {
      api
        .get("/jobs", { params: { page_size: 100, status: "open" } })
        .then(({ data }) => setJobs(data.items || []))
        .catch(() => setJobs([]));
    }
  }, [jobId, open]);

  useEffect(() => {
    if (!candidateId || !selectedJobId) {
      setReadiness(null);
      return;
    }
    let active = true;
    setReadiness(null);
    api
      .get("/submissions/internal/readiness", {
        params: { candidate_id: candidateId, job_id: selectedJobId },
      })
      .then(({ data }) => {
        if (!active) {
          return;
        }
        setReadiness(data);
        setForm((current) => ({
          ...current,
          resume_document_id: data.resume_versions?.[0]?.document_id || "",
          summary_version_id: data.summary_versions?.[0]?.id || "",
          screening_version_id: data.screening_versions?.[0]?.id || "",
        }));
      })
      .catch((error) => {
        if (active) {
          toast.error(apiErr(error));
        }
      });
    return () => {
      active = false;
    };
  }, [candidateId, selectedJobId]);

  const reset = () => {
    setCandidateId("");
    setReadiness(null);
    setForm({
      resume_document_id: "",
      summary_version_id: "",
      screening_version_id: "",
      salary_confirmation: "",
      availability_confirmation: "",
      submitted_comp: "",
    });
  };

  const basePayload = () => ({
    candidate_id: candidateId,
    job_id: selectedJobId,
    submitted_comp: form.submitted_comp ? Number(form.submitted_comp) : null,
  });

  const saveDraft = async () => {
    if (!candidateId || !selectedJobId) {
      toast.error("Select a candidate and assigned job first");
      return;
    }
    setBusy(true);
    try {
      await api.post("/submissions/drafts", basePayload());
      toast.success("Internal submission draft saved");
      reset();
      onDone?.();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const submitForReview = async () => {
    if (!candidateId || !selectedJobId) {
      toast.error("Select a candidate and assigned job first");
      return;
    }
    setBusy(true);
    try {
      await api.post("/submissions", { ...basePayload(), ...form });
      toast.success("Submitted for BDM review");
      reset();
      onDone?.();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
        data-testid="internal-submission-dialog"
      >
        <DialogHeader>
          <DialogTitle>Internal Submission</DialogTitle>
          <DialogDescription>
            Select the exact recruiter-ready record to send to the assigned BDM for review.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="internal-submission-candidate">Candidate</Label>
            <Select value={candidateId} onValueChange={setCandidateId}>
              <SelectTrigger
                id="internal-submission-candidate"
                data-testid="internal-submission-candidate"
              >
                <SelectValue placeholder="Select candidate" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((candidate) => (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    {candidate.full_name} — {candidate.current_title || "No title"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!jobId && (
            <div className="space-y-1.5">
              <Label htmlFor="internal-submission-job">Assigned job</Label>
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger id="internal-submission-job" data-testid="internal-submission-job">
                  <SelectValue placeholder="Select job" />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title} — {job.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {jobId && (
            <div className="space-y-1.5">
              <Label>Assigned job</Label>
              <div
                className="rounded-md border px-3 py-2 text-sm"
                data-testid="internal-submission-job-fixed"
              >
                {jobTitle || "Current job"}
              </div>
            </div>
          )}
        </div>

        {candidateId && selectedJobId && !readiness && (
          <div
            className="flex items-center gap-2 py-5 text-sm text-muted-foreground"
            data-testid="submission-readiness-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" /> Checking readiness…
          </div>
        )}

        {readiness && (
          <div className="space-y-4 border-t pt-4" data-testid="submission-readiness-panel">
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectionField
                label="Resume version"
                value={form.resume_document_id}
                onChange={(value) => setForm({ ...form, resume_document_id: value })}
                testId="submission-resume-version"
                options={readiness.resume_versions || []}
                valueKey="document_id"
                renderOption={(item) => `v${item.version || 1} — ${item.filename}`}
              />
              <SelectionField
                label="AI summary version"
                value={form.summary_version_id}
                onChange={(value) => setForm({ ...form, summary_version_id: value })}
                testId="submission-summary-version"
                options={readiness.summary_versions || []}
                valueKey="id"
                renderOption={(item) =>
                  `v${item.version || 1} — ${item.text?.slice(0, 48) || "Summary"}`
                }
              />
              <SelectionField
                label="Screening version"
                value={form.screening_version_id}
                onChange={(value) => setForm({ ...form, screening_version_id: value })}
                testId="submission-screening-version"
                options={readiness.screening_versions || []}
                valueKey="id"
                renderOption={(item) => `v${item.version || 1} — screening record`}
              />
              <div className="space-y-1.5">
                <Label htmlFor="submission-consent-status">Consent</Label>
                <div
                  id="submission-consent-status"
                  className="rounded-md border px-3 py-2 text-sm"
                  data-testid="submission-consent-status"
                >
                  {readiness.consent_recorded ? "Granted and recorded" : "Not recorded"}
                </div>
              </div>
              <SelectionField
                label="Salary status"
                value={form.salary_confirmation}
                onChange={(value) => setForm({ ...form, salary_confirmation: value })}
                testId="submission-salary-confirmation"
                options={CONFIRMATION_OPTIONS}
                valueKey="self"
                renderOption={(item) => item}
              />
              <SelectionField
                label="Availability status"
                value={form.availability_confirmation}
                onChange={(value) => setForm({ ...form, availability_confirmation: value })}
                testId="submission-availability-confirmation"
                options={CONFIRMATION_OPTIONS}
                valueKey="self"
                renderOption={(item) => item}
              />
            </div>

            <div className="grid gap-4 rounded-md border bg-muted/30 p-3 text-sm sm:grid-cols-3">
              <div data-testid="submission-salary-preview">
                <span className="block text-xs text-muted-foreground">Salary expectation</span>
                {readiness.salary_expectation ?? "Not recorded"} {readiness.currency || ""}
              </div>
              <div data-testid="submission-availability-preview">
                <span className="block text-xs text-muted-foreground">Availability</span>
                {readiness.availability || "Not recorded"}
              </div>
              <div data-testid="submission-notice-preview">
                <span className="block text-xs text-muted-foreground">Notice period</span>
                {readiness.notice_period || "Not recorded"}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="submission-compensation">Submitted compensation</Label>
              <Input
                id="submission-compensation"
                type="number"
                value={form.submitted_comp}
                placeholder="Optional"
                onChange={(event) => setForm({ ...form, submitted_comp: event.target.value })}
                data-testid="submission-compensation"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} data-testid="internal-submission-cancel">
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={busy || !candidateId || !selectedJobId}
            onClick={saveDraft}
            data-testid="internal-submission-save-draft"
          >
            <Save className="mr-1.5 h-4 w-4" /> Save Draft
          </Button>
          <Button
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            disabled={busy || !readiness}
            onClick={submitForReview}
            data-testid="internal-submission-submit-review"
          >
            {busy ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-1.5 h-4 w-4" />
            )}
            Submit for Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SelectionField({ label, value, onChange, testId, options, valueKey, renderOption }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={testId}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={testId} data-testid={testId}>
          <SelectValue
            placeholder={
              options.length
                ? `Select ${label.toLowerCase()}`
                : `No ${label.toLowerCase()} available`
            }
          />
        </SelectTrigger>
        <SelectContent>
          {options.map((item) => {
            const itemValue = valueKey === "self" ? item : item[valueKey];
            return (
              <SelectItem key={itemValue} value={itemValue}>
                {renderOption(item)}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
